import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useHistory, Link, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import styles from "./tag.module.scss";
import {useDispatch, useSelector} from "react-redux";
import ButtonFilled from "../../components/FormElements/ButtonFilled";
import ButtonOutlined from "../../components/FormElements/ButtonOutlined";
import branchApi from "../../store/branchStore/branchApi";
import CustomPagination from "../../components/CustomPagination";
import Loader from "../../components/Loader";
import store from "../../store";
import TagCustomerFormModal from "../../components/TagCustomerFormModal";
import {BadgeTag} from "../../components/Badge";
import { Roles } from "../../config";
import { useCurrentBranch } from "../../hooks/useCurrentBranch";
import GroupConfigWarning from "../../components/GroupConfigWarning";

const LIMIT = 5

const RESTRICTED_TAG_BUSINESS_IDS = ["1990319019407708160", "2024707981702074368", "2024709367416229888", "1990320835134492672", "2034216763209551872", "2034213686763065344", "2037054083965128704"];

const penIcon = (
  <svg width="25" height="20" viewBox="0 0 25 20" fill="currentColor">
    <path d="M10.8753 16.4141C10.7365 16.5529 10.5114 16.5529 10.3725 16.4141L7.2455 13.2878C7.10661 13.1489 7.10661 12.9238 7.2455 12.7849L15.5222 4.51013C15.661 4.37131 15.8861 4.37131 16.025 4.51013L19.152 7.63643C19.2909 7.77529 19.2909 8.00046 19.152 8.13932L10.8753 16.4141ZM6.47287 14.2215C6.5564 13.9707 6.87465 13.8955 7.06158 14.0824L9.57744 16.5977C9.76441 16.7846 9.68922 17.1029 9.43837 17.1865L5.6664 18.4423C5.3885 18.5348 5.12419 18.2705 5.21675 17.9926L6.47287 14.2215ZM20.52 6.77646L20.1418 7.1546C20.0029 7.29342 19.7778 7.29342 19.639 7.1546L16.5071 4.0235C16.3683 3.88464 16.3683 3.65947 16.5071 3.52061L16.8853 3.14259C17.5195 2.50813 18.548 2.50813 19.1822 3.14259L20.52 4.48008C21.1496 5.11627 21.1496 6.14047 20.52 6.77646Z" fill="currentColor"/>
  </svg>
)
const trashIcon = (
  <svg width="13" height="16" viewBox="0 0 13 16" fill="currentColor">
    <path d="M8.4375 2.375H11.25C11.8125 2.375 12.1875 2.75 12.1875 3.3125V4.25H0V3.3125C0 2.75 0.46875 2.375 0.9375 2.375H3.75C3.9375 1.34375 4.96875 0.5 6.09375 0.5C7.21875 0.5 8.25 1.34375 8.4375 2.375ZM4.6875 2.375H7.5C7.3125 1.8125 6.65625 1.4375 6.09375 1.4375C5.53125 1.4375 4.875 1.8125 4.6875 2.375ZM0.9375 5.1875H11.25L10.4062 14.6562C10.4062 15.125 9.9375 15.5 9.46875 15.5H2.71875C2.25 15.5 1.875 15.125 1.78125 14.6562L0.9375 5.1875Z" fill="currentColor"/>
  </svg>
)

const Tag = () => {
  const history = useHistory()
  const { id } = useParams()
  const dispatch = useDispatch()
  const { info } = useSelector(state => state.branchReducer)
  const { currentRole } = useCurrentBranch()
  const {t} = useTranslation()
  const isTagEditRestricted = RESTRICTED_TAG_BUSINESS_IDS.includes(String(info?.id)) && currentRole !== Roles.ADMINISTRATOR;
  const [tag, setTag] = useState(null)
  const [userId, setUserId] = useState('')
  const tags = useSelector(state => state?.branchReducer?.info?.configuration?.tags) || []
  const [customersData, setCustomersData] = useState(null)
  const [customersDataFetching, setCustomersDataFetching] = useState(null)
  const [deletingUser, setDeletingUser] = useState(false)
  const [isCustomerFormModalOpen, setIsCustomerFormModalOpen] = useState(false)
  const fileInputRef = useRef(null)
  const [isLoadingFile, setIsLoadingFile] = useState(false)
  const [params, setParams] = useState({
    limit: LIMIT,
    page: 1,
    skip: 0,
  })

  const getTagCustomers = async (tagId, params) => {
    try {
      setCustomersDataFetching(true)
      const response = await branchApi.getTagCustomers(info?.id, tagId, params)
      setCustomersData(response.data || null)
    } catch (e) {

    } finally {
      setCustomersDataFetching(false)
    }
  }

  useEffect(() => {
    if (tags?.length && id) {
      if (tag) {
        getTagCustomers(tag.id, params)
      } else {
        const foundTag = tags.find((tag) => tag.id === id)
        if (foundTag?.id) {
          setTag(foundTag)
          getTagCustomers(foundTag.id, params)
        } else {
          history.push('/profile/tags')
        }
      }
    }
  }, [tags, id, params])

  const onSkip = (quantityOfSkip, cb) => {
    setParams({...params, skip: quantityOfSkip})
    cb();
  }

  const handleDeleteUser = async (userId) => {
    try {
      if (info.id) {
        setDeletingUser(true)
        await branchApi.deleteBranchTagUser(info.id, id, userId)
        await getTagCustomers(id, params)
      }
    } catch (e) {
      const res = e.response ? e.response.data : null;

      store.addNotification({
        title: `Error code: ${res ? res.code : 'NULL'}`,
        message: `${res ? res.message : t('Notify.notRespond')}`,
        type: "danger",
        insert: "top",
        container: "top-right",
        animationIn: ["animate__animated", "animate__fadeIn"],
        animationOut: ["animate__animated", "animate__fadeOut"],
        dismiss: {
          duration: 5000,
          onScreen: true
        }
      });
    } finally {
      setDeletingUser(false)
    }
  }

  const handleSuccessTagCustomerFormSubmit = useCallback(async () => {
    setUserId('')
    setIsCustomerFormModalOpen(false)
    await getTagCustomers(tag.id, params)
  }, [params, tag])

  const handleLoadFiles = useCallback(async (file) => {
    if (info.id && tag.id) {
      try {
        setIsLoadingFile(true)
        const formData = new FormData()
        formData.append('file', file)
        formData.append('branch_tags', tag.id)
        await branchApi.postBranchTagBulk(info.id, formData)
        await getTagCustomers(tag.id, params)
      } finally {
        setIsLoadingFile(false)
      }
    }

  }, [params, tag, info])

  const handleButtonClick = () => {
    fileInputRef.current?.click(); // триггерим клик на input
  }

  return (
    <div className={styles.tag}>
      <GroupConfigWarning branch={info} />
      <Link
        to="/profile/tags"
        style={{ color: 'black', textDecoration: 'none', display: 'flex', gap: '16px', alignItems: 'center' }}
      >
        <i className="fa fa-chevron-left"
           aria-hidden="true"
           onClick={() => history.push('')}
        />
        <span>{t('returnBack')}</span>
      </Link>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div className={styles.item}>
          <div className={styles.name}>{t('title')}</div>
          <div className={styles.value} style={{ background: tag?.background_color, color: tag?.color }}>
            {tag?.name}
          </div>
        </div>

        <div className={styles.item}>
          <div className={styles.name}>{t('NSTag.bonusPrecent')}</div>
          <div className={styles.value}>
            {tag?.bonus_percentage}%
          </div>
        </div>

        <div className={styles.item}>
          <div className={styles.name}>{t('NSTag.userCount')}</div>
          <div className={styles.value}>
            {tag?.user_count}
          </div>
        </div>
      </div>

      <h3 style={{ fontFamily: 'graphic, serif', fontSize: 22, marginBottom: 0 }}>{t('NSTag.userList')}</h3>

      <div style={{ display: 'flex', gap: 15 }}>
        {!isTagEditRestricted && (
        <div style={{ flexShrink: 0 }}>
          <ButtonFilled onClick={() => setIsCustomerFormModalOpen(true)}>
            {t('addClient')}
          </ButtonFilled>
        </div>
        )}
        <div style={{ flexShrink: 0 }}>
          <ButtonOutlined variant="green" onClick={handleButtonClick}>
            <input
              type="file"
              accept=".xlsx,.xls"
              // multiple
              onChange={(e) => {
                const file = e.target.files[0];
                handleLoadFiles(file)
              }}
              ref={fileInputRef}
              hidden
            />
            {t('importFile')}
          </ButtonOutlined>
        </div>
      </div>

      <div style={{position: 'relative'}}>
        {(customersDataFetching || deletingUser) && (
          <div className={styles.loader}>
            <Loader color="#000" style={{zIndex: 100, opacity: 1}}/>
          </div>
        )}
        <table className="table">
          <thead>
          <tr>
            <th>№</th>
            <th>{t('NSTag.clientFullName')}</th>
            <th>{t('NSCorporative.phone')}</th>
            <th>{t('NSTag.tagOthers')}</th>
            <th>{t('action')}</th>
          </tr>
          </thead>
          <tbody>
          {customersData?.users?.map((user, index) => (
            <tr key={user.id + `${index}`}>
              <td>{params.skip + index + 1}</td>
              <td>{user.first_name} {user.last_name}</td>
              <td>{user.phone}</td>
              <td style={{maxWidth: 200}}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '6px 0' }}>
                  {user.user_tags?.map((tag) => (
                    <BadgeTag style={{ backgroundColor: tag.background_color, color: tag.color }} key={tag.id}>
                      {tag.name}
                    </BadgeTag>
                  ))}
                </div>
              </td>
              {!isTagEditRestricted && (
              <td style={{ width: 80 }}>
                <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <button
                    className={styles.actionBtn}
                    onClick={() => {
                      setUserId(user.id)
                      setIsCustomerFormModalOpen(true)
                    }}
                  >
                    {penIcon}
                  </button>
                  <button className={styles.actionDeleteBtn} onClick={() => !deletingUser && handleDeleteUser(user.id)}>
                    {trashIcon}
                  </button>
                </div>
              </td>
              )}
            </tr>
          ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: "100%", flexWrap: "wrap" }}>
        <CustomPagination
          count={customersData?.total_count}
          limit={customersData?.limit}
          max={4}
          loading={customersDataFetching}
          updated={false}
          page={params.page}
          setPage={(v) => setParams({...params, page: v, skip: ((v - 1) * customersData?.limit) || 0 })}
          onSkip={(_, cb) => cb()}
        />
      </div>

      <TagCustomerFormModal
        userId={userId}
        handleSuccessFormSubmit={handleSuccessTagCustomerFormSubmit}
        users={customersData?.users || []}
        setUserId={setUserId}
        isOpen={isCustomerFormModalOpen}
        setIsOpen={setIsCustomerFormModalOpen}
      />
    </div>
  )
}

export default Tag